import React, { SyntheticEvent, useState, useEffect } from 'react';

// ** MUI Imports
import Wrapper from '../../../components/Wrapper';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import 'react-datepicker/dist/react-datepicker.css';
import Box from '@mui/material/Box';

import { NavLink, Link, useNavigate, useParams } from 'react-router-dom';
import Title from '../../../components/Title';
import axios from 'axios';
import CancelIcon from '@mui/icons-material/Cancel';
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const ClientesEdit: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [idnumber, setIdnumber] = useState('');
  const [fullname, setFullname] = useState('');
  const [phone, setPhone] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [mainaddress, setMainaddress] = useState('');
  const [secondaddress, setSecondaddress] = useState('');
  const [landmark, setLandmark] = useState('');
  const [job, setJob] = useState('');
  const [coclient_fullname, setCoclient_fullname] = useState('');
  const [coclient_idnumber, setCoclient_idnumber] = useState('');
  const [coclient_address, setCoclient_address] = useState('');
  const [coclient_phone, setCoclient_phone] = useState('');
  const [office_id, setOffice_id] = useState('');
  const [collect_id, setCollect_id] = useState('');
  const [lends, setLends] = useState([]);
  const [collects, setCollects] = useState<string>('');
  const [collectsOffice, setCollectsOffice] = useState([]);
  const [offices, setOffices] = useState([]);

  const [initialState, setInitialState] = useState({
    idnumber: '',
    fullname: '',
    phone: '',
    cellphone: '',
    mainaddress: '',
    secondaddress: '',
    landmark: '',
    job: '',
    coclient_fullname: '',
    coclient_idnumber: '',
    coclient_address: '',
    coclient_phone: '',
    office_id: '',
    collect_id: '',
  });

  const [isModified, setIsModified] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const { data } = await axios.get(`clients/${id}`);
        const clientData = {
          idnumber: data.client.idnumber,
          fullname: data.client.fullname,
          phone: data.client.phone,
          cellphone: data.client.cellphone,
          mainaddress: data.client.mainaddress,
          secondaddress: data.client.secondaddress,
          landmark: data.client.landmark ?? '',
          job: data.client.job,
          coclient_fullname: data.client.coclient_fullname,
          coclient_idnumber: data.client.coclient_idnumber,
          coclient_address: data.client.coclient_address,
          coclient_phone: data.client.coclient_phone,
          office_id: data.client.collect.office.id,
          collect_id: data.client.collect.id,
        };
        setInitialState(clientData);
        setIdnumber(clientData.idnumber);
        setFullname(clientData.fullname);
        setPhone(clientData.phone);
        setCellphone(clientData.cellphone);
        setMainaddress(clientData.mainaddress);
        setSecondaddress(clientData.secondaddress);
        setLandmark(clientData.landmark);
        setJob(clientData.job);
        setCoclient_fullname(clientData.coclient_fullname);
        setCoclient_idnumber(clientData.coclient_idnumber);
        setCoclient_address(clientData.coclient_address);
        setCoclient_phone(clientData.coclient_phone);
        setOffice_id(clientData.office_id);
        setCollect_id(clientData.collect_id);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar 3 segundos
      } catch (error) {
        showAlert('Error fetching data!', 'error');
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(data.data);
      } catch (error) {
        showAlert('No existen oficinas creadas!', 'warning');
      }
    };
    fetchOffices();
  }, [showAlert]);

  useEffect(() => {
    if (office_id) {
      const fetchCollectsOffice = async () => {
        try {
          const { data } = await axios.get(`collectsoffice/${office_id}`);
          setCollectsOffice(data);
        } catch (error) {
          showAlert('No existen cobros asociados a la oficinas!', 'warning');
        }
      };
      fetchCollectsOffice();
    }
    setLends([]);
    setCollects('');
  }, [office_id, showAlert]);

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<any>>, fieldName: keyof typeof initialState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setter(value);
    if (initialState[fieldName] !== value) {
      setIsModified(true);
    }
  };

  const handleSelectChange = (setter: React.Dispatch<React.SetStateAction<any>>, fieldName: keyof typeof initialState) => (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    setter(value);
    if (initialState[fieldName] !== value) {
      setIsModified(true);
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault(); // Evita el envío del formulario por defecto
    if (!idnumber || !fullname || !phone || !cellphone || !mainaddress || !job) {
      showAlert('Tiene campos pendientes por diligenciar', 'warning');
      return;
    }
    showLoading();
    const formData = {
      idnumber,
      fullname,
      phone,
      cellphone,
      mainaddress,
      secondaddress,
      landmark: landmark.trim() || null,
      job,
      coclient_fullname,
      coclient_idnumber,
      coclient_address,
      coclient_phone,
      office_id,
      collect_id,
    };
    const url = `/clients/${id}`;

    try {
      const response = await axios.put(url, formData);
      showAlert('Se ha realizado el registro exitosamente!', 'success');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate('/administrar/clientes');
    } catch (error: any) {
      showAlert('Error fetching data!', 'error');
    } finally {
      hideLoading();
    }
  };

  document.title = "Editar Cliente - Mis Rutas";

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Informacion Cliente</Title>
      </Box>
      <Card sx={{ mb: { xs: 10, md: 12 } }}>
        <Divider sx={{ margin: 0 }} />
        <form onSubmit={submit}>
          <CardContent sx={{ pb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Datos Oficina
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} xl={6}>
                <FormControl fullWidth size='small'>
                  <InputLabel id="form-layouts-separator-select-label">
                    Seleccionar Oficina
                  </InputLabel>
                  {offices && offices.length > 0 ? (
                    <Select
                      size='small'
                      label="Seleccionar Oficina"
                      value={office_id}
                      id="form-layouts-separator-select"
                      labelId="form-layouts-separator-select-label"
                      onChange={handleSelectChange(setOffice_id, 'office_id')}
                      required
                    >
                      {offices.map((office: Office) => {
                        return (
                          <MenuItem key={office.id} value={office.id}>
                            {office.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} xl={6}>
                <FormControl fullWidth size='small'>
                  <InputLabel id="collect">Seleccionar Cobro</InputLabel>
                  {collectsOffice && collectsOffice.length > 0 ? (
                    <Select
                      size='small'
                      label="Seleccionar Cobro"
                      value={collect_id}
                      id="collects_id"
                      labelId="collects_id"
                      onChange={handleSelectChange(setCollect_id, 'collect_id')}
                      required
                    >
                      {collectsOffice.map((collect: Collect) => (
                        <MenuItem key={collect.id} value={collect.id}>
                          {collect.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ marginBottom: 0 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Datos Cliente
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='number' label='Cédula Cliente' placeholder='143526799' id="idnumber" value={idnumber} onChange={handleInputChange(setIdnumber, 'idnumber')} inputProps={{ maxLength: 20, readOnly: true }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='text' label='Nombre Cliente' placeholder='Ramon Perez' id="fullname" value={fullname} onChange={handleInputChange(setFullname, 'fullname')} inputProps={{ maxLength: 30 }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='tel' label='Teléfono' placeholder='6011234567' id="phone" value={phone} onChange={handleInputChange(setPhone, 'phone')} inputProps={{ maxLength: 10 }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='tel' label='Celular' placeholder='3101234567' id="cellphone" value={cellphone} onChange={handleInputChange(setCellphone, 'cellphone')} inputProps={{ maxLength: 10 }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='text' label='Dirección' placeholder='' id="mainaddress" value={mainaddress} onChange={handleInputChange(setMainaddress, 'mainaddress')} inputProps={{ maxLength: 30 }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='text' label='Dirección 2' placeholder='' id="secondaddres" value={secondaddress} onChange={handleInputChange(setSecondaddress, 'secondaddress')} inputProps={{ maxLength: 30 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size='small'
                  type='text'
                  label='Punto de referencia'
                  placeholder='Ej. Casa blanca frente al parque'
                  id="landmark"
                  value={landmark}
                  onChange={handleInputChange(setLandmark, 'landmark')}
                  inputProps={{ maxLength: 255 }}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='text' label='Oficio' placeholder='Desarrollador' id="job" value={job} onChange={handleInputChange(setJob, 'job')} inputProps={{ maxLength: 30 }} required />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ marginBottom: 0 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Datos Codeudor
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' label='Cédula Codeudor' placeholder='143526799' id="coclient_idnumber" value={coclient_idnumber} onChange={handleInputChange(setCoclient_idnumber, 'coclient_idnumber')} inputProps={{ maxLength: 20, readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' type='text' label='Nombre Codeudor' placeholder='Rufino Rufiares' id="coclient_fullname" value={coclient_fullname} onChange={handleInputChange(setCoclient_fullname, 'coclient_fullname')} inputProps={{ maxLength: 30 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' label='Teléfono Codeudor' placeholder='+1-123-456-8790' id="coclient_phone" value={coclient_phone} onChange={handleInputChange(setCoclient_phone, 'coclient_phone')} inputProps={{ maxLength: 10 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size='small' label='Dirección Codeudor' placeholder='' id="coclient_address" value={coclient_address} onChange={handleInputChange(setCoclient_address, 'coclient_address')} inputProps={{ maxLength: 30 }} />
              </Grid>
            </Grid>
          </CardContent>
          <Divider sx={{ margin: 0 }} />
          <CardActions sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
            <Button size='small' type='submit' sx={{ mr: 2 }} variant='contained' disabled={!isModified}>
              Guardar
            </Button>
            <Link to='/administrar/clientes'>
              <Button size='small' color='secondary' variant='outlined'>
                Cancel
              </Button>
            </Link>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default ClientesEdit;
